import {format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval} from 'date-fns';
import {useAnimatedPopper} from 'platform/components';
import {
  Box,
  HStack,
  Heading,
  Hide,
  Icon,
  Show,
  Text,
  VStack,
  getSize,
  useDevice,
} from 'platform/foundation';
import {convertStringLocaleToDateFns, useFormatCurrency, useLocale} from 'platform/locale';
import {css, useTheme} from 'styled-components';

import {useState, useCallback} from 'react';

import {DEFAULT_CURRENCY} from '@dms/shared';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CALENDAR_CONSTANTS} from '../constants/calendarConstants';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {createGroupedEventsWithCollisions} from '../utils/createGroupedEventsWithCollisions';
import {RentalEventRows} from './RentalEventRows';
import {RentalEventTooltip} from './RentalEventTooltip';
import {RentalVehicleTooltip} from './RentalVehicleTooltip';

interface RentalResourceCalendarContentProps extends RequiredTestIdProps {
  currentDate: Date;
  events: RentalResourceCalendarEvent[];
  isReadOnly?: boolean;
  onEventClick?: (event: RentalResourceCalendarEvent) => void;
}

export function RentalResourceCalendarContent(props: RentalResourceCalendarContentProps) {
  const theme = useTheme();

  const device = useDevice();

  const {language} = useLocale();
  const formatCurrency = useFormatCurrency();

  const [hoveredEvent, setHoveredEvent] = useState<RentalResourceCalendarEvent | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  const {openPopper, closePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: 'top',
    gutter: 0,
    isOutsideClickIgnored: true,
  });

  const {
    openPopper: openVehiclePopper,
    closePopper: closeVehiclePopper,
    referenceRef: vehicleReferenceRef,
    popperProps: vehiclePopperProps,
    Popper: VehiclePopper,
  } = useAnimatedPopper({
    placement: 'right',
    gutter: 0,
    isOutsideClickIgnored: true,
  });

  const handleEventMouseEnter = useCallback(
    (event: RentalResourceCalendarEvent) => {
      if (device === 'tablet' || device === 'mobile') {
        return;
      }
      setHoveredEvent(event);
      openPopper();
    },
    [device, openPopper]
  );

  const handleEventMouseLeave = useCallback(() => {
    if (device === 'tablet' || device === 'mobile') {
      return;
    }
    setHoveredEvent(null);
    closePopper();
  }, [closePopper, device]);

  const handleVehicleMouseEnter = useCallback(
    (index: number, event: RentalResourceCalendarEvent) => {
      if (device === 'tablet' || device === 'mobile') {
        return;
      }

      setHoveredEvent(event);
      setHoveredRowIndex(index);
      openVehiclePopper();
    },
    [device, openVehiclePopper]
  );

  const handleVehicleMouseLeave = useCallback(() => {
    if (device === 'tablet' || device === 'mobile') {
      return;
    }
    setHoveredEvent(null);
    closeVehiclePopper();
  }, [closeVehiclePopper, device]);

  const monthStart = startOfMonth(props.currentDate);
  const monthEnd = endOfMonth(props.currentDate);
  const daysInMonth = eachDayOfInterval({start: monthStart, end: monthEnd});
  const groupedEvents = createGroupedEventsWithCollisions(props.currentDate, props.events);

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      data-testid={suffixTestId('resourceView', props)}
    >
      <div
        css={css`
          overflow-x: auto;
          overflow-y: hidden;
          width: 100%;
          height: 100%;
        `}
      >
        <div
          css={css`
            min-width: ${CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH +
            daysInMonth.length * CALENDAR_CONSTANTS.DAY_COLUMN_WIDTH}px;
            height: 100%;
          `}
        >
          <div
            css={css`
              display: grid;
              grid-template-columns: ${CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH}px 1fr;
              height: ${CALENDAR_CONSTANTS.HEADER_HEIGHT}px;
              background-color: ${theme.colors.general.transparent};
              position: sticky;
              top: 0;
              z-index: ${theme.zIndices.BIG_CALENDAR_RESOURCE_TITLE_HEADER};
            `}
          >
            <div
              css={css`
                padding: ${getSize(2)};
                border-right: 1px solid ${theme.colors.general.separator};
                border-bottom: 1px solid ${theme.colors.general.separator};
                position: sticky;
                left: 0;
                z-index: ${theme.zIndices.BIG_CALENDAR_HEADER};
                background-color: ${theme.colors.palettes.neutral[10][100]};
              `}
            />
            <div
              css={css`
                display: grid;
                border-bottom: 1px solid ${theme.colors.general.separator};
                grid-template-columns: repeat(
                  ${daysInMonth.length},
                  ${CALENDAR_CONSTANTS.DAY_COLUMN_WIDTH}px
                );
                border-right: 1px solid ${theme.colors.general.separator};
              `}
            >
              {daysInMonth.map((day: Date) => (
                <Box
                  key={day.toISOString()}
                  padding={1}
                  borderRight="1px solid"
                  borderColor={
                    format(day, 'i') === '7' ? 'palettes.neutral.50.100' : 'general.separator'
                  }
                >
                  <VStack spacing={0} align="center" justify="center">
                    <Heading size={5} color={isSameDay(day, new Date()) ? 'warning' : undefined}>
                      {format(day, 'EEE', {locale: convertStringLocaleToDateFns(language)})}
                    </Heading>
                    <Text
                      color={isSameDay(day, new Date()) ? 'warning' : 'tertiary'}
                      alternative={isSameDay(day, new Date())}
                    >
                      {format(day, 'd')}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </div>
          </div>
          <div
            css={css`
              position: absolute;
              top: ${CALENDAR_CONSTANTS.HEADER_HEIGHT}px;
              left: 0;
              width: ${CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH}px;
              height: calc(100% - ${CALENDAR_CONSTANTS.HEADER_HEIGHT}px);
              z-index: ${theme.zIndices.BIG_CALENDAR_RESOURCE_TITLE};
              pointer-events: none;
              background-color: ${theme.colors.palettes.neutral[10][100]};
            `}
          >
            {groupedEvents.map((groupedEvent, groupedEventIndex) => (
              <div
                key={`sticky-${groupedEvent.groupId}-${groupedEvent.id}`}
                ref={
                  hoveredEvent?.rentalOrder?.vehicleId === groupedEvent.rentalOrder?.vehicleId
                    ? vehicleReferenceRef
                    : undefined
                }
                css={css`
                  width: 100%;
                  height: ${groupedEvent.expandedRowHeight}px;
                  border-right: 1px solid ${theme.colors.general.separator};
                  border-bottom: 1px solid ${theme.colors.palettes.neutral[20][100]};
                  background-color: ${hoveredRowIndex === groupedEventIndex
                    ? theme.colors.palettes.neutral[20][100]
                    : theme.colors.palettes.neutral[10][100]};
                  padding: ${getSize(2)};
                  pointer-events: auto;
                `}
                onMouseEnter={() => handleVehicleMouseEnter(groupedEventIndex, groupedEvent)}
                onMouseLeave={handleVehicleMouseLeave}
              >
                <VStack spacing={1}>
                  <Heading size={5} color="primary" alternative isSingleLine>
                    {groupedEvent.rentalOrder?.vehicleTitle}
                  </Heading>
                  <HStack align="center" spacing={1}>
                    <Icon size={4} value="sidebar/vehicles" color="palettes.black.900.70" />
                    <Text size="xSmall" color="secondary">
                      {groupedEvent.rentalOrder?.licensePlate || EMPTY_PLACEHOLDER}
                    </Text>
                    <Icon size={4} value="action/payment" color="palettes.black.900.70" />
                    <Show when={groupedEvent.rentalOrder?.dailyRate?.amount}>
                      <Text size="xSmall" color="secondary">
                        {formatCurrency(
                          groupedEvent.rentalOrder?.dailyRate?.amount ?? 0,
                          groupedEvent.rentalOrder?.dailyRate?.currency ?? DEFAULT_CURRENCY
                        )}
                      </Text>
                    </Show>
                    <Hide when={groupedEvent.rentalOrder?.dailyRate?.amount}>
                      {EMPTY_PLACEHOLDER}
                    </Hide>
                  </HStack>
                </VStack>
              </div>
            ))}
            {Array.from(
              {length: Math.max(0, CALENDAR_CONSTANTS.MINIMUM_ROWS - groupedEvents.length)},
              (_, index) => index
            ).map((index) => {
              const emptyRowIndex = groupedEvents.length + index;
              return (
                <div
                  key={`sticky-empty-${index}`}
                  css={css`
                    width: 100%;
                    height: ${CALENDAR_CONSTANTS.ROW_HEIGHT}px;
                    border-right: 1px solid ${theme.colors.general.separator};
                    border-bottom: 1px solid ${theme.colors.palettes.neutral[20][100]};
                    background-color: ${hoveredRowIndex === emptyRowIndex
                      ? theme.colors.palettes.neutral[20][100]
                      : theme.colors.palettes.neutral[10][100]};
                    pointer-events: auto;
                  `}
                />
              );
            })}
          </div>
          <RentalEventRows
            data-testid={suffixTestId('resourceView', props)}
            events={props.events}
            currentDate={props.currentDate}
            hoveredRowIndex={hoveredRowIndex}
            referenceRef={referenceRef}
            hoveredEvent={hoveredEvent}
            isReadOnly={props.isReadOnly}
            onEventClick={props.onEventClick}
            onEventMouseEnter={handleEventMouseEnter}
            onEventMouseLeave={handleEventMouseLeave}
            onRowMouseEnter={setHoveredRowIndex}
            onRowMouseLeave={() => setHoveredRowIndex(null)}
          />
        </div>
      </div>
      <Popper
        {...popperProps}
        floatingStyles={{
          ...popperProps.floatingStyles,
          zIndex: theme.zIndices.TOOLTIP,
        }}
      >
        {hoveredEvent && (
          <RentalEventTooltip data-testid={suffixTestId('body', props)} event={hoveredEvent} />
        )}
      </Popper>
      <VehiclePopper
        {...vehiclePopperProps}
        floatingStyles={{
          ...vehiclePopperProps.floatingStyles,
          zIndex: theme.zIndices.TOOLTIP,
        }}
      >
        {hoveredEvent?.rentalOrder?.vehicleId && (
          <RentalVehicleTooltip
            data-testid={suffixTestId('body', props)}
            vehicleId={hoveredEvent.rentalOrder.vehicleId}
          />
        )}
      </VehiclePopper>
    </Box>
  );
}
